// src/schemas/paymentFormSchema.ts

import { z } from 'zod';

const validateCreditCard = (cardNumber: string): boolean => {
    const cleanNumber = cardNumber.replace(/\D/g, '');

    if (!cleanNumber || cleanNumber.length < 13 || cleanNumber.length > 19) {
        return false;
    }

    let sum = 0;
    let shouldDouble = false;

    for (let i = cleanNumber.length - 1; i >= 0; i--) {
        let digit = parseInt(cleanNumber.charAt(i), 10);

        if (shouldDouble) {
            digit *= 2;
            if (digit > 9) {
                digit -= 9;
            }
        }

        sum += digit;
        shouldDouble = !shouldDouble;
    }

    return sum % 10 === 0;
};

const validateExpirationDate = (month: string, year: string): boolean => {
    if (!month || !year) return true;

    const monthNum = parseInt(month, 10);
    const yearNum = parseInt(year, 10);

    if (monthNum < 1 || monthNum > 12) return false;

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;

    const fullYear = yearNum < 100 ? 2000 + yearNum : yearNum;

    if (fullYear < currentYear) return false;
    if (fullYear === currentYear && monthNum < currentMonth) return false;

    return true;
};

export const paymentFormSchema = z.object({
    cardHolderName: z
        .string()
        .min(1, 'Full name is required')
        .min(2, 'Full name must be at least 2 characters')
        .max(100, 'Full name must be less than 100 characters')
        .regex(/^(?! )[a-zA-Z\s'-]+$/, 'Full name must not start with a space and can only contain letters, spaces, hyphens, and apostrophes')
        .refine((val) => val.trim().length > 0, {
            message: 'Full name cannot be only spaces',
        }),

    cardNumber: z
        .string()
        .min(1, 'Card number is required')
        .transform((val) => val.replace(/\D/g, ''))
        .refine((val) => val.length >= 13, {
            message: 'Card number must be at least 13 digits',
        })
        .refine((val) => val.length <= 19, {
            message: 'Card number must be at most 19 digits',
        })
        .refine((val) => validateCreditCard(val), {
            message: 'Please enter a valid card number',
        }),

    expirationMonth: z
        .string()
        .min(1, 'Expiration month is required')
        .refine((val) => {
            const month = parseInt(val, 10);
            return !isNaN(month) && month >= 1 && month <= 12;
        }, {
            message: 'Please enter a valid month (01-12)',
        })
        .transform((val) => {
            return val.padStart(2, '0');
        }),

    expirationYear: z
        .string()
        .min(1, 'Expiration year is required')
        .refine((val) => {
            const year = parseInt(val, 10);
            if (isNaN(year)) return false;

            const currentYear = new Date().getFullYear();

            let fullYear = year;
            if (year < 100) {
                fullYear = 2000 + year;
            }

            return fullYear >= currentYear && fullYear <= currentYear + 50;
        }, {
            message: 'Please enter a valid year',
        })
        .transform((val) => {
            const year = parseInt(val, 10);
            return year < 100 ? String(2000 + year) : val;
        }),

    ccv: z
        .string()
        .min(1, 'CVV is required')
        .min(3, 'CVV must be at least 3 digits')
        .max(4, 'CVV must be at most 4 digits')
        .regex(/^\d+$/, 'CVV can only contain numbers'),
}).refine((data) => {
    if (data.expirationMonth && data.expirationYear) {
        return validateExpirationDate(data.expirationMonth, data.expirationYear);
    }
    return true;
}, {
    message: 'Card has expired or expiration date is invalid',
    path: ['expirationMonth'],
});

export type PaymentFormSchema = z.infer<typeof paymentFormSchema>;